import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  X,
} from 'lucide-react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { Button, DropdownMenu } from 'slab'
import { capitalize } from 'utils'
import {
  getGetBrokersTaskWorkflowsIdQueryKey,
  usePostBrokersTaskWorkflowsIdAction,
} from 'utils/web_api'
import {
  type BrokersWorkflowAvailableActionsItem,
  type BrokersWorkflowSummary,
} from 'utils/web_api.schemas'
import { invalidateWorkflowQueries } from 'utils/workflowCacheUtils'

import {
  sortAvailableActions,
} from './WorkflowVisualIndicators'

// Workflow Action Controls Component
const WorkflowActionControls = ({
  workflow,
  onSuccess,
}: {
  workflow: BrokersWorkflowSummary
  onSuccess?: (updatedWorkflow: BrokersWorkflowSummary) => void
}) => {
  const { memberId } = useContext(BrokersMemberContext)
  const [selectedAction, setSelectedAction] = useState<
    BrokersWorkflowAvailableActionsItem | undefined
  >()
  const queryClient = useQueryClient()

  // Memoize sorted actions to prevent unnecessary recalculation
  const sortedActions = useMemo(
    () => sortAvailableActions(workflow.available_actions),
    [workflow.available_actions]
  )

  // Track previous available actions to detect real changes
  const prevWorkflowActionsRef = useRef<BrokersWorkflowAvailableActionsItem[]>(
    []
  )

  // Auto-select first available action ONLY when available actions actually change
  useEffect(() => {
    const prevActions = prevWorkflowActionsRef.current
    const currentActions = workflow.available_actions || []

    // Check if the available actions actually changed (not just a re-render)
    const actionsChanged =
      prevActions.length !== currentActions.length ||
      prevActions.some((action, index) => action !== currentActions[index])

    if (actionsChanged) {
      // Actions changed - update the ref and reset to first action
      prevWorkflowActionsRef.current = currentActions

      if (sortedActions && sortedActions.length > 0) {
        setSelectedAction(sortedActions[0])
      } else {
        setSelectedAction(undefined)
      }
    }
  }, [workflow.available_actions, sortedActions])

  const mutation = usePostBrokersTaskWorkflowsIdAction()

  const handleExecuteAction = () => {
    if (!selectedAction) return

    const requestData: any = {
      id: workflow.id,
      action: selectedAction,
    }

    // Add cancellation_reason for cancel action
    if (selectedAction === 'cancel') {
      const reason = prompt('Enter cancellation reason (optional):')
      // If user clicked Cancel in the prompt, abort the cancellation
      if (reason === null) {
        return
      }
      if (reason) {
        requestData.data = { cancellation_reason: reason }
      }
    }

    mutation.mutate(requestData, {
      onSuccess: (updatedWorkflow) => {
        // Update the individual workflow cache directly with returned data
        // This provides immediate UI feedback for the specific query variant
        queryClient.setQueryData(
          getGetBrokersTaskWorkflowsIdQueryKey(workflow.id),
          updatedWorkflow
        )

        // Invalidate all workflow-related queries using centralized utility
        invalidateWorkflowQueries(queryClient, {
          workflowId: workflow.id,
          enrollmentId: workflow.insurance_enrollment_id,
          memberId,
        })

        // Call the optional onSuccess callback
        onSuccess?.(updatedWorkflow)
      },
      onError: (error) => {
        console.error('Failed to execute workflow action:', error)
        alert('Failed to execute action. Please try again.')
      },
    })
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'start':
        return 'Start'
      case 'complete':
        return 'Complete'
      case 'cancel':
        return 'Cancel'
      case 'pause':
        return 'Pause'
      case 'resume':
        return 'Resume'
      case 'reopen':
        return 'Reopen'
      default:
        return capitalize(action)
    }
  }

  const WorkflowActionButton = ({
    selectedAction,
    isPending,
    handleExecuteAction,
  }: {
    selectedAction: BrokersWorkflowAvailableActionsItem | undefined
    isPending: boolean
    handleExecuteAction: () => void
  }) => {
    const getActionIcon = () => {
      if (isPending) return <Loader2 className='w-4 h-4 animate-spin' />

      switch (selectedAction) {
        case 'start':
        case 'resume':
          return <Play className='w-4 h-4' />
        case 'pause':
          return <Pause className='w-4 h-4' />
        case 'cancel':
          return <X className='w-4 h-4' />
        case 'complete':
          return <Check className='w-4 h-4' />
        case 'reopen':
          return <Play className='w-4 h-4' />
        case 'rollback':
          return <RotateCcw className='w-4 h-4' />
        default:
          return <Play className='w-4 h-4' />
      }
    }

    return (
      <Button
        size='small'
        variant='primary'
        onClick={handleExecuteAction}
        disabled={!selectedAction || isPending}
      >
        {getActionIcon()}
      </Button>
    )
  }

  if (!workflow.available_actions || workflow.available_actions.length === 0) {
    return (
      <div className='flex items-center space-x-2'>
        <Button variant='secondary' size='small' disabled>
          No Actions Available
        </Button>
        <WorkflowActionButton
          selectedAction={undefined}
          isPending={false}
          handleExecuteAction={() => {}}
        />
      </div>
    )
  }

  return (
    <div className='flex items-center space-x-2'>
      <DropdownMenu
        trigger={
          <Button variant='secondary' size='small' rightIcon={ChevronDown}>
            {selectedAction ? getActionLabel(selectedAction) : 'Select Action'}
          </Button>
        }
      >
        {workflow.available_actions.map((action) => (
          <DropdownMenu.Item
            key={action}
            onSelect={() => setSelectedAction(action)}
          >
            {getActionLabel(action)}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu>

      <WorkflowActionButton
        selectedAction={selectedAction}
        isPending={mutation.isPending}
        handleExecuteAction={handleExecuteAction}
      />
    </div>
  )
}

export default WorkflowActionControls
